import { AdesaoStatus } from '../../enum/adesao-status.enum';
import { CampanhaStatus } from '../../enum/campanha-status.enum';
import { TIPO_CAMPANHA_LABEL, TipoCampanhaBack } from '../../enum/tipo-campanha-back.enum';
import { TipoDestinoCampanha } from '../../enum/tipo-destino-campanha.enum';
import { Adesao } from '../../models/adesao.model';
import { Campanha, CampanhaFiltros } from '../../models/campanha.model';
import {
  CampanhaFormValue,
  createCampanhaFormDefaultValue,
} from '../../models/campanha-form.model';
import { DestinoCampanha } from '../../models/destino-campanha.model';
import { ImportarParticipantesResponse } from '../../models/participante-importacao.model';
import {
  CampanhaListQueryParams,
  CampanhaResponseDto,
  CreateCampanhaCommandDto,
  ImportarParticipantesResponseDto,
  PerfilParticipanteDto,
  QuestionarioResponseDto,
  UpdateCampanhaCommandDto,
} from '../models';

/** API literal for questionnaire destination (confirmed local/HML). */
const TP_DESTINO_QUESTIONARIO = 'Q';

export interface CampanhaCommandMapperOptions {
  /** Código do status: 'R' (Rascunho) ou 'AG' (Agendada) — únicas transições disparadas pelo form. */
  cdStatus: string;
  /** Required for create — must be > 0 and reference a valid Oracle FK (G-11/G-21). */
  codCampanha: number;
  codEmprs?: number | null;
  seq?: number;
  /** Round-trip from GET response on update — omit on create unless explicitly set. */
  dtaEfetivacao?: string | null;
  /** Round-trip from GET response on update — omit on create unless explicitly set. */
  dtaAlteracao?: string | null;
}

export function apiFlagToBoolean(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toUpperCase();
  return normalized === 'S' || normalized === 'SIM';
}

export function booleanToApiFlag(value: boolean): string {
  return value ? 'S' : 'N';
}

export function tpDestinoCampToTipoDestino(
  tpDestinoCamp: string | null | undefined,
): TipoDestinoCampanha | '' {
  if (!tpDestinoCamp) {
    return '';
  }

  const normalized = tpDestinoCamp.trim().toUpperCase();

  if (normalized === TP_DESTINO_QUESTIONARIO) {
    return TipoDestinoCampanha.Suitability;
  }

  return '';
}

export function tipoDestinoToTpDestinoCamp(tipo: TipoDestinoCampanha | ''): string {
  if (tipo === TipoDestinoCampanha.Suitability) {
    return TP_DESTINO_QUESTIONARIO;
  }

  return '';
}

export function extractDatePart(dateTime: string | null | undefined): string {
  if (!dateTime) {
    return '';
  }

  return dateTime.slice(0, 10);
}

export function extractTimePart(dateTime: string | null | undefined): string {
  if (!dateTime) {
    return '';
  }

  const timeSeparatorIndex = dateTime.indexOf('T');
  if (timeSeparatorIndex < 0) {
    return '';
  }

  return dateTime.slice(timeSeparatorIndex + 1, timeSeparatorIndex + 6);
}

export function combineDateAndTime(date: string, time: string): string {
  const trimmedDate = date?.trim() ?? '';
  if (!trimmedDate) {
    return '';
  }

  const trimmedTime = time?.trim() ?? '';
  const normalizedTime = trimmedTime || '00:00';
  const withSeconds =
    normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime;

  return `${trimmedDate}T${withSeconds}`;
}

/** ASP.NET rejects empty strings for DateTime? — send null when date is unset. */
export function toApiDateTimeOrNull(date: string, time: string): string | null {
  const combined = combineDateAndTime(date, time);
  return combined || null;
}

/** Required DateTime fields — never produce empty string for ASP.NET binding. */
export function toApiDateTimeRequired(date: string, time: string): string | null {
  return toApiDateTimeOrNull(date, time);
}

export function toPositiveIntOrNull(
  value: number | string | null | undefined,
): number | null {
  if (value == null || value === '') {
    return null;
  }

  const parsed =
    typeof value === 'number' ? value : Number.parseInt(String(value).trim(), 10);

  if (Number.isNaN(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

export function resolveCampanhaStatus(status: string | null | undefined): CampanhaStatus {
  const values: string[] = Object.values(CampanhaStatus);
  return status && values.includes(status) ? (status as CampanhaStatus) : CampanhaStatus.Rascunho;
}

const CAMPANHA_STATUS_LABEL_FALLBACK: Record<CampanhaStatus, string> = {
  [CampanhaStatus.Rascunho]: 'Rascunho',
  [CampanhaStatus.Agendada]: 'Agendada',
  [CampanhaStatus.AtivoPortal]: 'Ativa (Portal)',
  [CampanhaStatus.Encerrada]: 'Encerrada',
  [CampanhaStatus.Cancelada]: 'Cancelada',
  [CampanhaStatus.Pausada]: 'Pausada',
};

export function mapCampanhaResponseToCampanha(dto: CampanhaResponseDto): Campanha {
  // Rótulo do "Tipo de Campanha" (decisão do PO, 2026-08-10): sempre o texto curto de
  // TIPO_CAMPANHA_LABEL, nunca o texto do catálogo GET /v1/Campanha/TiposCampanhas — a chamada
  // ao catálogo continua acontecendo (CampanhaService.listarTiposCampanha), só não influencia
  // mais este rótulo.
  const tipo =
    dto.codCampanha != null
      ? (TIPO_CAMPANHA_LABEL[dto.codCampanha as TipoCampanhaBack] ?? String(dto.codCampanha))
      : '';
  const status = resolveCampanhaStatus(dto.status);

  return {
    id: dto.idCampanha != null ? String(dto.idCampanha) : '',
    nome: dto.nmCampanha ?? '',
    status,
    // Prefere o texto vindo da API (dto.descricaoStatus); cai para o mapa local só se faltar.
    statusLabel: dto.descricaoStatus?.trim() || CAMPANHA_STATUS_LABEL_FALLBACK[status],
    dataInicio: extractDatePart(dto.dtaIni),
    horaInicio: extractTimePart(dto.dtaIni),
    dataFim: extractDatePart(dto.dtaFim),
    horaFim: extractTimePart(dto.dtaFim),
    dataInicioVigencia: extractDatePart(dto.dtIniVigPartic),
    dataIntegracao: dto.dtIntegracao ? extractDatePart(dto.dtIntegracao) : null,
    horaIntegracao: extractTimePart(dto.dtIntegracao),
    idOperadorIntegracao: dto.idOperadorIntegr ?? null,
    tipo,
  };
}

function parsePlanoNumPlbnf(form: CampanhaFormValue): number | null {
  if (form.selecionarTodosPlanos) {
    return null;
  }

  const firstPlano = form.planosAdicionados[0];
  if (!firstPlano?.codigo) {
    return null;
  }

  return toPositiveIntOrNull(firstPlano.codigo);
}

function parseQuestionarioId(destinoId: string): number | null {
  return toPositiveIntOrNull(destinoId);
}

function resolveIdQuestionario(form: CampanhaFormValue): number | null {
  if (form.tipoDestino !== TipoDestinoCampanha.Suitability) {
    return null;
  }

  return parseQuestionarioId(form.destinoId);
}

function mapCampanhaFormValueToCommandFields(
  form: CampanhaFormValue,
  options: CampanhaCommandMapperOptions,
): CreateCampanhaCommandDto {
  const command: CreateCampanhaCommandDto = {
    seq: options.seq ?? 1,
    codCampanha: options.codCampanha,
    codEmprs: toPositiveIntOrNull(options.codEmprs),
    nmCampanha: form.nome,
    nmExibicao: form.nomeExibicao,
    descricao: form.descricao,
    instrucao: form.instrucoes || null,
    cdStatus: options.cdStatus,
    dtaIni: toApiDateTimeRequired(form.dataInicio, form.horarioInicio),
    dtaFim: toApiDateTimeOrNull(form.dataFim, form.horarioFim),
    dtIniVigPartic: toApiDateTimeOrNull(
      form.participanteDataInicio,
      form.participanteHorarioInicio,
    ),
    exibeTermo: booleanToApiFlag(form.exibeTermo),
    tpDestinoCamp: tipoDestinoToTpDestinoCamp(form.tipoDestino),
    idQuestionario: resolveIdQuestionario(form),
    idPerfilInvestimentoDef: form.idPerfilInvestimentoDef ?? null,
    urlCampanha: form.linkCampanha || null,
    numPlbnf: parsePlanoNumPlbnf(form),
  };

  if (options.dtaEfetivacao !== undefined) {
    command.dtaEfetivacao = options.dtaEfetivacao;
  }

  if (options.dtaAlteracao !== undefined) {
    command.dtaAlteracao = options.dtaAlteracao;
  }

  return command;
}

export function mapCampanhaResponseToFormValue(dto: CampanhaResponseDto): CampanhaFormValue {
  const defaults = createCampanhaFormDefaultValue();
  const tipoDestino = tpDestinoCampToTipoDestino(dto.tpDestinoCamp);
  const destinoId = dto.idQuestionario != null ? String(dto.idQuestionario) : '';
  const planosAdicionados =
    dto.numPlbnf != null
      ? [
          {
            id: String(dto.numPlbnf),
            nome: `Plano ${dto.numPlbnf}`,
            codigo: String(dto.numPlbnf),
          },
        ]
      : [];

  return {
    ...defaults,
    id: dto.idCampanha != null ? String(dto.idCampanha) : undefined,
    status: resolveCampanhaStatus(dto.status),
    nome: dto.nmCampanha ?? '',
    nomeExibicao: dto.nmExibicao ?? '',
    descricao: dto.descricao ?? '',
    instrucoes: dto.instrucao ?? '',
    dataInicio: extractDatePart(dto.dtaIni),
    dataFim: extractDatePart(dto.dtaFim),
    horarioInicio: extractTimePart(dto.dtaIni),
    horarioFim: extractTimePart(dto.dtaFim),
    participanteDataInicio: extractDatePart(dto.dtIniVigPartic),
    participanteHorarioInicio: extractTimePart(dto.dtIniVigPartic),
    dtIntegracao: dto.dtIntegracao ?? '',
    exibeTermo: apiFlagToBoolean(dto.exibeTermo),
    tipoDestino,
    destinoId,
    idPerfilInvestimentoDef: dto.idPerfilInvestimentoDef ?? null,
    linkCampanha: dto.urlCampanha?.trim() ?? '',
    planosAdicionados,
  };
}

export function mapCampanhaFormValueToCreateCommand(
  form: CampanhaFormValue,
  options: CampanhaCommandMapperOptions,
): CreateCampanhaCommandDto {
  return mapCampanhaFormValueToCommandFields(form, options);
}

export function mapCampanhaFormValueToUpdateCommand(
  form: CampanhaFormValue,
  options: CampanhaCommandMapperOptions,
): UpdateCampanhaCommandDto {
  const command = mapCampanhaFormValueToCommandFields(form, options);
  const parsedId = Number.parseInt(form.id ?? '', 10);

  if (Number.isNaN(parsedId)) {
    return command;
  }

  return { ...command, idCampanha: parsedId };
}

/**
 * `status`, `nome` e `idCampanha` vão para a query (`status`, `nmCampanha` e `idCampanha`).
 * Os três também são reaplicados no cliente (`CampanhaService.filtrarCampanhas`), como rede de
 * proteção caso o endpoint ainda não honre algum deles. Não inferimos `ativa` a partir do
 * status — correlacionar as duas coisas no front é justamente o que saiu de escopo.
 *
 * `tipo` e o período ficam inteiramente no cliente. O período em especial: a tela filtra por
 * sobreposição (`periodoDe`/`periodoAte` vs. `dataInicio`/`dataFim` da campanha), enquanto o
 * endpoint expõe só `dtaIni` — uma data única, cuja semântica (match exato ou "a partir de")
 * ainda não foi confirmada com o backend. Mapear o range para esse parâmetro antes disso
 * mudaria o resultado da busca, então o filtro segue client-side.
 */
export function mapCampanhaFiltrosToQueryParams(
  filtros: CampanhaFiltros,
): CampanhaListQueryParams {
  const params: CampanhaListQueryParams = {};

  if (filtros.status && filtros.status !== 'todos') {
    params.status = filtros.status;
  }

  const nome = filtros.nome?.trim();
  if (nome) {
    params.nmCampanha = nome;
  }

  if (filtros.idCampanha != null) {
    params.idCampanha = filtros.idCampanha;
  }

  return params;
}

/**
 * Códigos de `StatusTrocaPerfil` (backend). `/v1/PerfilParticipante` devolve a descrição, mas os
 * endpoints de Histórico/Portal devolvem o código — por isso os dois formatos são aceitos.
 */
const ADESAO_CODIGO_MAP: Record<string, AdesaoStatus> = {
  P: AdesaoStatus.Pendente,
  I: AdesaoStatus.Pendente,
  S: AdesaoStatus.Solicitado,
  F: AdesaoStatus.Concluido,
  C: AdesaoStatus.Cancelada,
};

/**
 * Caminho inverso, para os endpoints que recebem o status como código — hoje a exportação de
 * participantes. Pendente sai como 'P': 'I' (Iniciado) também é lido como Pendente na entrada,
 * mas o filtro leva um código só. 'F' (Finalizado) é o que o back usa para Concluído.
 */
const ADESAO_STATUS_CODIGO: Record<AdesaoStatus, string> = {
  [AdesaoStatus.Pendente]: 'P',
  [AdesaoStatus.Solicitado]: 'S',
  [AdesaoStatus.Concluido]: 'F',
  [AdesaoStatus.Cancelada]: 'C',
};

/** `undefined` para "todos" ou status desconhecido — aí o filtro simplesmente não vai na query. */
export function adesaoStatusParaCodigo(status: string | null | undefined): string | undefined {
  const texto = status?.trim();
  if (!texto || texto === 'todos') {
    return undefined;
  }

  return ADESAO_STATUS_CODIGO[texto as AdesaoStatus];
}

const ADESAO_DESCRICAO_MAP: Record<string, AdesaoStatus> = {
  pendente: AdesaoStatus.Pendente,
  iniciado: AdesaoStatus.Pendente,
  solicitado: AdesaoStatus.Solicitado,
  finalizado: AdesaoStatus.Concluido,
  cancelado: AdesaoStatus.Cancelada,
};

function mapApiAdesaoStatus(
  apiStatus: string | null | undefined,
  idPerfilSelPart: number | null | undefined,
): AdesaoStatus {
  const texto = apiStatus?.trim() ?? '';

  if (texto) {
    // Código de 1 letra tem prioridade; a descrição cobre o endpoint que manda texto.
    const porCodigo = texto.length === 1 ? ADESAO_CODIGO_MAP[texto.toUpperCase()] : undefined;
    return porCodigo ?? ADESAO_DESCRICAO_MAP[texto.toLowerCase()] ?? AdesaoStatus.Pendente;
  }

  if (idPerfilSelPart == null || idPerfilSelPart === 0) {
    return AdesaoStatus.Pendente;
  }

  return AdesaoStatus.Concluido;
}

export function mapQuestionarioResponseToDestinoCampanha(
  dto: QuestionarioResponseDto,
): DestinoCampanha {
  const id = dto.idQuestionario != null ? String(dto.idQuestionario) : '';
  const nome = dto.nome?.trim() || (id ? `Questionário ${id}` : 'Questionário');

  return {
    id,
    nome,
    tipoDestino: TipoDestinoCampanha.Suitability,
  };
}

export function mapPerfilParticipanteToAdesao(dto: PerfilParticipanteDto): Adesao {
  const matricula = dto.numMatrPartf;
  const nome =
    dto.nomeParticipante?.trim() ||
    (matricula != null ? `Participante ${matricula}` : 'Participante');

  return {
    id: dto.id != null ? String(dto.id) : '',
    campanhaId: dto.idCampanha != null ? String(dto.idCampanha) : '',
    nome,
    matricula: matricula ?? null,
    registro: dto.registration ?? null,
    nomePlano: dto.nomePlano?.trim() || null,
    dataAssinaturaTermo: dto.dataConfirmacaoTermo ? extractDatePart(dto.dataConfirmacaoTermo) : null,
    status: mapApiAdesaoStatus(dto.status, dto.idPerfilSelPart),
    idPerfilAnterior: dto.idPerfilAnt ?? null,
    idPerfilNovo: dto.idPerfilAtingido ?? null,
    statusDescricao: dto.status?.trim() || null,
    numProtocoloSolicitacao: dto.numProtocoloSolicitacao ?? null,
    dataProtocoloSolicitacao: dto.dataProtocoloSolicitacao ?? null,
    numProtocoloCancelamento: dto.numProtocoloCancelamento ?? null,
    dataProtocoloCancelamento: dto.dataProtocoloCancelamento ?? null,
  };
}

export function mapImportarParticipantesResponseToModel(
  dto: ImportarParticipantesResponseDto,
): ImportarParticipantesResponse {
  return {
    totalProcessados: dto.totalProcessados,
    totalImportados: dto.totalImportados,
    totalRejeitados: dto.totalRejeitados,
    rejeitados: dto.rejeitados.map((rejeitado) => ({
      linha: rejeitado.linha,
      identificador: rejeitado.identificador,
      motivo: rejeitado.motivo,
    })),
  };
}
